import React, { useState } from 'react';
import axios from 'axios';
import { Truck, Plus, Mail, Pencil, Trash2, Check, X } from 'lucide-react';
import { fmtDate } from '../lib/orderMoney';
import { CopyButton } from './CopyButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2 text-[13px] outline-none focus:border-neutral-400';

const ShipForm = ({ init, onSave, onCancel, busy, testPrefix, saveLabel, showNotify }) => {
  const [v, setV] = useState({ tracking_number: '', carrier: '', note: '', send_email: true, ...init });
  const set = (k) => (e) => setV({ ...v, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input data-testid={`${testPrefix}-tracking`} value={v.tracking_number} onChange={set('tracking_number')} placeholder="Tracking number" className={inputCls + ' font-mono'} />
        <input data-testid={`${testPrefix}-carrier`} value={v.carrier} onChange={set('carrier')} placeholder="Carrier (USPS, DHL…)" className={inputCls} />
      </div>
      <input data-testid={`${testPrefix}-note`} value={v.note} onChange={set('note')} maxLength={300} placeholder="Note for customer, e.g. contains items 1–2 (optional)" className={inputCls} />
      <div className="flex flex-wrap items-center gap-3">
        {showNotify && (
          <label className="inline-flex items-center gap-1.5 text-[12px] text-neutral-600 dark:text-neutral-300">
            <input data-testid={`${testPrefix}-notify`} type="checkbox" checked={v.send_email} onChange={set('send_email')} /> Email customer now
          </label>
        )}
        <div className="ml-auto flex gap-2">
          {onCancel && <button onClick={onCancel} className="rounded-md border border-neutral-300 dark:border-neutral-700 px-3 py-1.5 text-[12.5px]"><X size={13} /></button>}
          <button data-testid={`${testPrefix}-save`} disabled={busy || !v.tracking_number.trim()} onClick={() => onSave(v)}
            className="inline-flex items-center gap-1.5 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 text-[12.5px] px-3 py-1.5 rounded-md disabled:opacity-50">
            <Truck size={13} /> {busy ? 'Saving…' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const ShipRow = ({ s, i, order, headers, apply, setMsg }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState('');
  const call = async (kind, fn) => {
    setBusy(kind); setMsg('');
    try { const { data } = await fn(); apply(data); return data; }
    catch (e) { setMsg(e?.response?.data?.detail || 'Something went wrong'); return null; }
    finally { setBusy(''); }
  };
  const base = `${API}/orders/${order.id}/shipments/${s.id}`;
  if (editing) return (
    <div className="p-3"><ShipForm init={s} busy={busy === 'save'} testPrefix={`ship-edit-${s.id}`} saveLabel="Save"
      onCancel={() => setEditing(false)} onSave={async (v) => { if (await call('save', () => axios.patch(base, v, { headers }))) setEditing(false); }} /></div>
  );
  return (
    <div data-testid={`shipment-${s.id}`} className="p-3 flex flex-wrap items-start gap-3">
      <div className="w-7 h-7 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 grid place-items-center text-[11px] font-bold shrink-0">{i + 1}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 flex-wrap">
          <span className="font-mono text-[13px] text-neutral-900 dark:text-neutral-100 break-all">{s.tracking_number}</span>
          {s.carrier && <span className="text-[12px] text-neutral-500">· {s.carrier}</span>}
          <CopyButton text={s.tracking_number} label="" testId={`copy-tracking-${s.id}`} />
        </div>
        {s.note && <div className="text-[12px] text-neutral-600 dark:text-neutral-400">{s.note}</div>}
        <div className="text-[11px] mt-0.5">{s.emailed_at
          ? <span className="text-emerald-600 inline-flex items-center gap-1"><Check size={11} /> Customer notified {fmtDate(s.emailed_at)}</span>
          : <span className="text-amber-600">Customer not notified yet</span>}</div>
      </div>
      <div className="flex items-center gap-1">
        <button data-testid={`notify-shipment-${s.id}`} disabled={!!busy} title="Email this tracking to the customer"
          onClick={async () => { const d = await call('notify', () => axios.post(`${base}/notify`, {}, { headers })); if (d) setMsg(`Tracking email sent to ${d.sent_to}.`); }}
          className="inline-flex items-center gap-1 border border-neutral-300 dark:border-neutral-700 text-[12px] px-2.5 py-1.5 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-60">
          <Mail size={12} /> {busy === 'notify' ? 'Sending…' : s.emailed_at ? 'Resend' : 'Notify'}
        </button>
        <button data-testid={`edit-shipment-${s.id}`} onClick={() => setEditing(true)} className="p-1.5 text-neutral-500 hover:text-brand"><Pencil size={13} /></button>
        <button data-testid={`delete-shipment-${s.id}`} disabled={!!busy}
          onClick={() => window.confirm('Remove this tracking number?') && call('del', () => axios.delete(base, { headers }))}
          className="p-1.5 text-neutral-500 hover:text-red-600"><Trash2 size={13} /></button>
      </div>
    </div>
  );
};

export const ShipmentsPanel = ({ order, headers, onChange }) => {
  const ships = order.shipments || [];
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const apply = (d) => onChange({ shipments: d.shipments, status: d.status || order.status, tracking_number: d.tracking_number, carrier: d.carrier });
  const add = async (v) => {
    setBusy(true); setMsg('');
    try {
      const { data } = await axios.post(`${API}/orders/${order.id}/shipments`, v, { headers });
      apply(data); setAdding(false);
      setMsg(data.email_dispatched ? (data.email_configured ? 'Tracking added & customer emailed.' : 'Tracking added. Email not configured — skipped.') : 'Tracking added.');
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed to add tracking.'); } finally { setBusy(false); }
  };
  const showForm = adding || ships.length === 0;
  return (
    <div data-testid={`tracking-panel-${order.id}`} className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416]">
      {ships.length > 0 && (
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {ships.map((s, i) => <ShipRow key={s.id} s={s} i={i} order={order} headers={headers} apply={apply} setMsg={setMsg} />)}
        </div>
      )}
      <div className={`p-3 ${ships.length ? 'border-t border-neutral-100 dark:border-neutral-800' : ''}`}>
        {showForm
          ? <ShipForm key={ships.length} busy={busy} testPrefix={`ship-add-${order.id}`} showNotify
              saveLabel={ships.length ? `Add package ${ships.length + 1}` : 'Save & notify'}
              onCancel={ships.length ? () => setAdding(false) : null} onSave={add} />
          : <button data-testid={`add-shipment-btn-${order.id}`} onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-brand">
              <Plus size={14} /> Add another tracking number
            </button>}
        {msg && <div data-testid={`tracking-msg-${order.id}`} className="text-[11.5px] text-neutral-600 dark:text-neutral-400 mt-2">{msg}</div>}
      </div>
    </div>
  );
};
